// src/components/Navbar.js
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import IconButton from "@mui/material/IconButton";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import MenuIcon from "@mui/icons-material/Menu";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import "./component_styles/Navbar.css";
import useWindowDimensions from "../hooks/useWindowDimensions";

const navItems = [
  { label: "Home", to: "/" },
  { label: "Location", to: "/location" },
  { label: "Prescriptions", to: "/prescription" },
  { label: "Vaccinations", to: "/vaccination" },
  { label: "Minor Ailments", to: "/consultation" },
  { label: "Contact", to: "/contact" },
  { label: "About Us", to: "/about" },
];

// Routes where a colored bottom bar is shown
const pagesWithBottomBar = [
  "/vaccination",
  "/consultation",
  "/prescription",
  "/transfers",
  "/location",
  "/contact",
  "/about",
  "/my-profile",
  "/my-prescriptions",
  "/profile-completion",
  "/signin",
  "/signup",
];

export default function Navbar() {
  const location = useLocation();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("md"));
  const [value, setValue] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { width, height } = useWindowDimensions();

  // Sync active tab with URL
  useEffect(() => {
    const idx = navItems.findIndex((item) => item.to === location.pathname);
    setValue(idx >= 0 ? idx : 0);
  }, [location.pathname]);

  const isPageWithBottomBar = pagesWithBottomBar.includes(location.pathname);

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    )
      return;
    setDrawerOpen(open);
  };

  return (
    <>
      {/* Main Nav */}
      <Box
        component="nav"
        className={isPhone ? "navbar-phone" : "navbar"}
        sx={{ display: "flex", alignItems: "align-items" }}
      >
        {/* Logo */}
        <Link to="/" className={isPhone ? "logo-small" : "logo"}>
          <img src="assets/logo.png" className="w-[12rem]" alt="Logo" />
        </Link>

        {/* Hamburger on phone, full tabs on desktop */}
        {isPhone ? (
          <IconButton onClick={toggleDrawer(true)} sx={{ ml: "auto" }}>
            <MenuIcon />
          </IconButton>
        ) : (
          <Box sx={{ flex: 0.7 }}>
            <Tabs
              value={value}
              onChange={(e, newVal) => setValue(newVal)}
              variant="fullWidth"
              textColor="inherit"
              TabIndicatorProps={{
                style: { backgroundColor: "#333", height: 2, marginBottom: 4 },
              }}
              sx={{
                "& .MuiTab-root": {
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 500,
                  color: "#333",
                  textTransform: "none",
                  padding: "0 20px",
                  minHeight: "auto",
                },
                "& .Mui-selected": { color: "#333" },
              }}
            >
              {navItems.map((item) => (
                <Tab
                  key={item.to}
                  disableRipple
                  label={item.label}
                  component={Link}
                  to={item.to}
                  sx={{ marginTop: width > 1841 ? 1 : 0 }}
                />
              ))}
            </Tabs>
          </Box>
        )}
      </Box>

      {/* Bottom Bar for specific pages */}
      {isPageWithBottomBar && (
        <div
          style={{ height: "15px", backgroundColor: "#e4b8a5", width: "100%" }}
        />
      )}

      {/* Mobile Drawer */}
      <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)}>
        <Box
          sx={{ width: 250 }}
          role="presentation"
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          <List>
            {navItems.map((item) => (
              <ListItem button key={item.to} component={Link} to={item.to}>
                <ListItemText primary={item.label} />
              </ListItem>
            ))}
          </List>
          <Divider />
        </Box>
      </Drawer>
    </>
  );
}
